import { Home, LogOut } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import digavouchLogo from "@/assets/digavouch-logo.svg";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/hooks/useTenant";
import { PARTNER_LOGOS } from "@/lib/partnerLogos";
import { demoPartnerKeyForPath } from "@/lib/demoSurfaces";
import { DEMO_SURFACES_IN_BUILD } from "@/lib/env";
import { FEATHER_PARTNER_NAME } from "@/lib/featherReview";
import { DemoHomeButton, DemoSignOutButton } from "@/components/DemoSignOutButton";
import { StaffOrgSwitcher } from "@/components/StaffOrgSwitcher";


/**
 * Light co-branded banner shown across the applicant + voucher journey.
 * Left: partner logo (white pill) | DigaVouch shield + wordmark.
 * Right: Home / Log out links.
 *
 * Tenant branding is resolved at runtime by `useTenant()` from
 * `window.location.hostname`. On generic hosts (app.*, dev, previews,
 * unknown tenants) the header renders DigaVouch-only chrome — no
 * partner pill, no divider. There is intentionally no loading spinner;
 * the silent fallback during fetch matches the generic-mode UX.
 *
 * Public-flow routes (magic-link based — voucher/verify/oauth/results)
 * have no dashboard access, so the brand marks render as static
 * (non-clickable) and the Home + Log out nav items are suppressed.
 * See docs/cobranded-header-public-mode.md.
 */
const PUBLIC_PATH_PREFIXES = [
  "/article",
  "/feather",
  "/verify/",
  "/voucher-flow",
  "/verified",
  "/manual-review",
  "/oauth-success",
  "/oauth-error",
  "/login",
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) =>
    p.endsWith("/") ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`)
  );
}

export function CoBrandedHeader({ compactArticleFlow = false }: { compactArticleFlow?: boolean }) {
  const { tenant } = useTenant();
  const { user, signOut } = useOptionalAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const publicMode = isPublicPath(pathname);
  const forcedPartnerKey = demoPartnerKeyForPath(pathname, Boolean(user));
  // `DEMO_SURFACES_IN_BUILD` first, so a production build drops the demo
  // partner names and colours below.
  const forcedArticle = DEMO_SURFACES_IN_BUILD && forcedPartnerKey === "article";
  const featherForced = DEMO_SURFACES_IN_BUILD && forcedPartnerKey === "feather";

  const partnerKey = forcedPartnerKey ?? tenant?.branding?.partner_key;
  const partnerSrc = partnerKey ? PARTNER_LOGOS[partnerKey] : undefined;
  const hasTenant = !!partnerSrc && (!!forcedPartnerKey || !!tenant);
  const partnerName = featherForced ? FEATHER_PARTNER_NAME : forcedArticle ? "Article" : tenant?.name ?? "";
  const brandColor = featherForced ? "#1859c9" : forcedArticle ? "#ff6458" : tenant?.branding?.brand_color;

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const isLoggedIn = !!user;

  // Expose brand color as a CSS variable for future accent opt-in.
  const headerStyle = brandColor
    ? ({ ["--tenant-accent" as string]: brandColor } as React.CSSProperties)
    : undefined;

  // The partner layout with no partner logo: every recruiting org that is not
  // on a partner tenant host (Juan, 2026-10-16). DigaVouch's own mark and name
  // where the partner's would sit, and nothing else: no partner pill, no
  // partner name, no "with DigaVouch" attribution with nothing before it.
  if (compactArticleFlow && !partnerSrc) {
    return (
      <header className="co-branded-header article-flow-header relative z-10 w-full">
        <div className="co-branded-header-inner article-flow-header-inner">
          <div className="digavouch-flow-mark">
            <img src={digavouchLogo} alt="" aria-hidden="true" />
            <strong>DigaVouch</strong>
          </div>
        </div>
      </header>
    );
  }

  if (compactArticleFlow && partnerSrc) {
    return (
      <header className="co-branded-header article-flow-header relative z-10 w-full" style={headerStyle}>
        <div className="co-branded-header-inner article-flow-header-inner">
          <div className="partner-brand-mark article-flow-partner-mark">
            {/* Feather, live tenant or review route: the mark from the
                lockup, and the name as text, which reads better at this size
                than the lockup's own wordmark. The name is the tenant's own
                (from the API) on a live tenant. */}
            {partnerKey === "feather" ? (
              <>
                <span className="feather-logo-crop" aria-hidden="true"><img src={partnerSrc} alt="" /></span>
                <strong className="feather-partner-name">{partnerName}</strong>
              </>
            ) : (
              <img src={partnerSrc} alt={partnerName} />
            )}
          </div>
          <div className="article-flow-attribution" aria-label="with DigaVouch">
            <span>with</span>
            <img src={digavouchLogo} alt="" aria-hidden="true" />
            <strong>DigaVouch</strong>
          </div>
          {/* A demo review path is previewed by a signed-in operator: give
              them a way out. A live applicant or voucher never sees this
              (no demo path, no sign-in). */}
          {featherForced && <DemoHomeButton />}
          {forcedPartnerKey && <DemoSignOutButton />}
        </div>
      </header>
    );
  }

  return (
    <header
      className="co-branded-header relative z-10 w-full border-b border-slate-200/70 bg-white/85 text-slate-900 shadow-[0_1px_0_rgba(15,23,42,0.03)] backdrop-blur-md"
      style={headerStyle}
    >
      <div className="co-branded-header-inner max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <div className="co-branded-lockup flex items-center gap-4">
          {hasTenant && (
            <>
              {publicMode ? (
                <div className="partner-brand-mark rounded-2xl border border-slate-200 bg-white px-3 py-1.5 flex items-center shadow-sm">
                  <img
                    src={partnerSrc}
                    alt={partnerName}
                    className="h-6 sm:h-7 w-auto object-contain"
                  />
                </div>
              ) : (
                <button
                  onClick={() => navigate("/")}
                  className="partner-brand-mark rounded-2xl border border-slate-200 bg-white px-3 py-1.5 flex items-center shadow-sm cursor-pointer"
                >
                  <img
                    src={partnerSrc}
                    alt={partnerName}
                    className="h-6 sm:h-7 w-auto object-contain"
                  />
                </button>
              )}
              <div className="co-brand-divider h-7 w-px bg-slate-200" aria-hidden />
            </>
          )}
          {publicMode ? (
            <div className="digavouch-brand-mark flex items-center gap-2">
              <img
                src={digavouchLogo}
                alt="DigaVouch"
                className="h-8 w-8 rounded-md"
              />
              <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950">
                DIGAVOUCH
              </span>
            </div>
          ) : (
            <button
              onClick={() => navigate("/")}
              className="digavouch-brand-mark flex items-center gap-2 group cursor-pointer"
            >
              <img
                src={digavouchLogo}
                alt="DigaVouch"
                className="h-8 w-8 rounded-md"
              />
              <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950 group-hover:text-slate-700 transition-colors">
                DIGAVOUCH
              </span>
            </button>
          )}
        </div>

        <nav className="hidden sm:flex items-center gap-7 text-sm font-medium text-slate-600">
          {/* DigaVouch staff only (the server decides who that is; the
              component renders nothing for anyone else). Operator surfaces
              only: a demo review path shows the applicant view. */}
          {!publicMode && isLoggedIn && <StaffOrgSwitcher />}
          {!publicMode && (
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 hover:text-slate-900 transition-colors cursor-pointer"
            >
              <Home className="h-4 w-4" />
              Home
            </button>
          )}
          {/* Log out also on a demo review path, which counts as public (no
              Home) but is only ever viewed by a signed-in operator. */}
          {(!publicMode || forcedPartnerKey) && isLoggedIn && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 hover:text-slate-900 transition-colors"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
